import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@mui/material';
import ExersiceTutorial from './ExersiceTutorial';
import '../../css/landing/explore.scss';
import demo from '../../icon/about/demo-video.mp4';

// Node graph: base positions live in SVG viewBox space (0-1200 x 0-600).
// Mouse proximity pulls nearby nodes toward the cursor and grows them slightly,
// which is why positions/edges are computed at runtime rather than hardcoded.
const GRAPH_NODES = [
    { x: 80, y: 120, r: 3, type: 'cool' },
    { x: 220, y: 60, r: 4, type: 'accent', pulse: true },
    { x: 340, y: 180, r: 2.5, type: 'cool' },
    { x: 500, y: 90, r: 3, type: 'cool' },
    { x: 600, y: 220, r: 4, type: 'accent', pulse: true },
    { x: 760, y: 70, r: 2.5, type: 'cool' },
    { x: 880, y: 160, r: 3.5, type: 'accent', pulse: true },
    { x: 1020, y: 100, r: 2.5, type: 'cool' },
    { x: 1120, y: 200, r: 3, type: 'cool' },
    { x: 150, y: 320, r: 2.5, type: 'cool' },
    { x: 950, y: 380, r: 3, type: 'cool' },
];

const GRAPH_EDGES = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
    [5, 6], [6, 7], [7, 8], [9, 2], [10, 6],
];

const GRAPH_INFLUENCE = 260; // svg units - how far the cursor's pull reaches
const GRAPH_MAX_PULL = 26; // svg units - max distance a node is dragged toward the cursor
const GRAPH_MAX_GROW = 2.4; // multiplier added to a node's radius at closest proximity
const GRAPH_EASE = 0.12; // spring easing per animation frame

const ExplorePanel = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [exercise, setExercise] = useState(""); // State for exercise
    const [currentStatIndex, setCurrentStatIndex] = useState(0); // State for which readout is highlighted

    const svgRef = useRef(null);
    const nodeRefs = useRef([]);
    const edgeRefs = useRef([]);
    const mouseRef = useRef(null); // {x,y} in svg coordinate space, or null when cursor is outside
    const graphState = useRef(GRAPH_NODES.map(() => ({ dx: 0, dy: 0, tdx: 0, tdy: 0, dr: 0, tdr: 0 })));
    const reducedMotionRef = useRef(false);

    const handleTutorialClick = (selectedExercise) => {
        setModalOpen(true);
        setExercise(selectedExercise);
    }

    // Drive the interactive node graph: nodes near the cursor get pulled toward
    // it and grow slightly, and their connecting lines brighten to match.
    useEffect(() => {
        reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotionRef.current) return undefined; // static graph for reduced-motion users

        let frameId;

        const tick = () => {
            const mouse = mouseRef.current;

            GRAPH_NODES.forEach((node, i) => {
                const state = graphState.current[i];
                if (mouse) {
                    const ddx = mouse.x - node.x;
                    const ddy = mouse.y - node.y;
                    const dist = Math.hypot(ddx, ddy);
                    const proximity = Math.max(0, 1 - dist / GRAPH_INFLUENCE);
                    if (proximity > 0) {
                        const norm = dist === 0 ? 0 : 1 / dist;
                        state.tdx = ddx * norm * GRAPH_MAX_PULL * proximity;
                        state.tdy = ddy * norm * GRAPH_MAX_PULL * proximity;
                        state.tdr = node.r * GRAPH_MAX_GROW * proximity;
                    } else {
                        state.tdx = 0; state.tdy = 0; state.tdr = 0;
                    }
                } else {
                    state.tdx = 0; state.tdy = 0; state.tdr = 0;
                }

                state.dx += (state.tdx - state.dx) * GRAPH_EASE;
                state.dy += (state.tdy - state.dy) * GRAPH_EASE;
                state.dr += (state.tdr - state.dr) * GRAPH_EASE;

                const circle = nodeRefs.current[i];
                if (circle) {
                    circle.setAttribute('cx', node.x + state.dx);
                    circle.setAttribute('cy', node.y + state.dy);
                    circle.setAttribute('r', Math.max(0.5, node.r + state.dr));
                }
            });

            GRAPH_EDGES.forEach(([a, b], i) => {
                const line = edgeRefs.current[i];
                if (!line) return;
                const sa = graphState.current[a];
                const sb = graphState.current[b];
                const na = GRAPH_NODES[a];
                const nb = GRAPH_NODES[b];
                line.setAttribute('x1', na.x + sa.dx);
                line.setAttribute('y1', na.y + sa.dy);
                line.setAttribute('x2', nb.x + sb.dx);
                line.setAttribute('y2', nb.y + sb.dy);
                const boost = Math.min(1, Math.max(sa.dr, sb.dr) / (na.r * GRAPH_MAX_GROW || 1));
                line.style.strokeOpacity = 0.28 + boost * 0.55;
                line.style.strokeWidth = 1 + boost * 1.6;
            });

            frameId = requestAnimationFrame(tick);
        };

        frameId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameId);
    }, []);

    const handleHeroMouseMove = (e) => {
        if (reducedMotionRef.current || !svgRef.current) return;
        const point = svgRef.current.createSVGPoint();
        point.x = e.clientX;
        point.y = e.clientY;
        const ctm = svgRef.current.getScreenCTM();
        if (!ctm) return;
        const svgPoint = point.matrixTransform(ctm.inverse());
        mouseRef.current = { x: svgPoint.x, y: svgPoint.y };
    };

    const handleHeroMouseLeave = () => {
        mouseRef.current = null;
    };

    const statistics = [{
        title: "Class Sessions Conducted",
        value: 543
    },
    {
        title: "Students Engaged in VizPI-Powered Sessions",
        value: 10759
    },
    {
        title: "Groups Formed in Real-Time Classes",
        value: 3468
    }];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStatIndex((prevIndex) => (prevIndex + 1) % statistics.length);
        }, 5000); // Cycle which readout is highlighted every 5 seconds
        return () => clearInterval(interval);
    }, []);

    return (
        <div>
            <section className="hero-viewport" onMouseMove={handleHeroMouseMove} onMouseLeave={handleHeroMouseLeave}>
                <svg
                    ref={svgRef}
                    className="hero-graph"
                    viewBox="0 0 1200 600"
                    preserveAspectRatio="xMidYMid slice"
                    aria-hidden="true">
                    {GRAPH_EDGES.map(([a, b], i) => (
                        <line
                            key={`edge-${a}-${b}`}
                            ref={(el) => { edgeRefs.current[i] = el; }}
                            x1={GRAPH_NODES[a].x}
                            y1={GRAPH_NODES[a].y}
                            x2={GRAPH_NODES[b].x}
                            y2={GRAPH_NODES[b].y}
                        />
                    ))}
                    {GRAPH_NODES.map((node, i) => (
                        <circle
                            key={`node-${i}`}
                            ref={(el) => { nodeRefs.current[i] = el; }}
                            cx={node.x}
                            cy={node.y}
                            r={node.r}
                            className={`vz-node vz-node--${node.type}${node.pulse ? ' vz-node-live' : ''}`}
                        />
                    ))}
                </svg>

                <div className="hero-content">
                    <span className="eyebrow">real-time peer learning analytics</span>
                    <Typography variant='h1' className="header-text">
                        Welcome to VizPI
                    </Typography>
                    <Typography variant='body1' className="intro-text hero-intro">
                    Our platform VizPI (visualizing peer interactions) is designed to help instructors conduct dynamic in-class coding exercises that not only engage students in meaningful peer interactions but also provide deep insights into their cognitive processes and understanding of programming concepts. By facilitating real-time collaboration, discussion, and problem-solving among students, these exercises go beyond traditional assignments, revealing critical aspects of students' mental models, teamwork skills, and learning progress.                       </Typography>

                    <div className="telemetry-row">
                        {statistics.map((stat, index) => (
                            <div className={`telemetry-cell${index === currentStatIndex ? ' is-active' : ''}`} key={stat.title}>
                                <span className="telemetry-dot"></span>
                                <Typography variant='h4' className="descirption-value">
                                    {stat.value.toLocaleString()}
                                </Typography>
                                <Typography variant='h6' className="descirption-text">
                                    {stat.title}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </div>

                <span className="hero-scroll-cue">scroll ↓</span>
            </section>

            <div className="intro-container">
                <hr className="division-line" />
                <br />
                <Typography variant='body1' className="intro-text">
                In today’s rapidly evolving educational landscape, it’s not enough to rely solely on assignment outcomes to gauge student understanding. The rich flow of information generated during peer interactions plays a vital role in learning programming, and ultimately, in future software development careers. However, existing tools often fall short in capturing and utilizing this data to provide context-aware support.
                </Typography>
                <br />
                <Typography variant='body1' className="intro-text">
                Our platform addresses this gap by continuously monitoring and interpreting students' interactions, giving you, the instructor, a clearer picture of where students may be struggling or excelling. This enables you to provide targeted feedback and interventions that are both timely and effective, ensuring that your students not only keep up with the material but truly master it. By leveraging this tool, you can guide your class more effectively, enhance learning outcomes, and prepare your students for the challenges of real-world software development.                </Typography>
                <br />
            <Typography variant='body1' className="intro-text">
                Click on the video below to see a brief intro on our platform.
            </Typography>
            <br />
                <hr className="division-line" />
                <br />
            </div>

            <div className="video-container">
                <div className="terminal-chrome">
                    <span className="term-dot term-dot--red"></span>
                    <span className="term-dot term-dot--amber"></span>
                    <span className="term-dot term-dot--green"></span>
                    <span className="term-path">~/vizpi/demo.mp4</span>
                </div>
                <div className="video-frame">
                    <iframe
                        src="https://www.youtube.com/embed/wkGMtVkP6oQ"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="YouTube Video"
                    ></iframe>
                </div>
            </div>

            <div className="intro-container">
            <br />
                <Typography variant='h2' className="exersice-header-text">
                    Exercise Overview
                </Typography>
                <Typography variant='body1' className="intro-text">
                    VizPI has developed a range of innovative exercise. Click on any exercise to view a brief tutorial on how to use it and to understand the distinctions between the instructor's and the student's perspectives.
                </Typography>
                <br />
                <hr className="division-line" />
            </div>
            <div className="module-grid">
                <div className="module-card" onClick={() => handleTutorialClick("Auto Grouping")}>
                    <div className="hexagon-background"></div>
                    <span className="module-tag">AUTO_GROUP</span>
                    <h3 className="module-title">Auto Grouping</h3>
                    <p className="module-desc">
                        Facilitates grouping mechanisms that allow for peer instructions and group discussions
                    </p>
                </div>
                <div className="module-card" onClick={() => handleTutorialClick("Audio Discussion")}>
                    <div className="hexagon-background"></div>
                    <span className="module-tag">AUDIO_DISC</span>
                    <h3 className="module-title">Audio Discussion</h3>
                    <p className="module-desc">
                        Generates transcripts of real-time discussions and provides AI-generated insights into the progress of the conversations
                    </p>
                </div>
                <div className="module-card" onClick={() => handleTutorialClick("Helper/Helpee")}>
                    <div className="hexagon-background"></div>
                    <span className="module-tag">HELPER_HELPEE</span>
                    <h3 className="module-title">Helper/Helpee</h3>
                    <p className="module-desc">
                        Allows students who have mastered the tasks to assist struggling peers through a chat system
                    </p>
                </div>
                <div className="module-card" onClick={() => handleTutorialClick("VizMental")}>
                    <div className="hexagon-background"></div>
                    <span className="module-tag">VIZMENTAL</span>
                    <h3 className="module-title">Vizmental</h3>
                    <p className="module-desc">
                        Provides a personalized AI assistant that guides and encourages students throughout their coding process
                    </p>
                </div>
            </div>
            {modalOpen && <ExersiceTutorial exercise={exercise} onClose={() => setModalOpen(false)} />}
        </div>
    );
};

export default ExplorePanel;
